import React, { useState } from 'react';
import api from '../services/api';
import { 
  X, Wallet, Smartphone, ShieldCheck, Landmark, Copy, Info, 
  CheckCircle, Clock, Check 
} from 'lucide-react';

interface PaymentModalProps {
  paymentData: {
    id: string; // publicationId or caseId
    title?: string;
    amount: number;
    type: string;
  };
  onClose: () => void;
  onSuccess: () => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ paymentData, onClose, onSuccess }) => {
  const [gateway, setGateway] = useState<'JazzCash' | 'EasyPaisa' | 'BankTransfer'>('JazzCash');
  const [senderName, setSenderName] = useState('');
  const [senderPhone, setSenderPhone] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [subscriberEmail, setSubscriberEmail] = useState(() => {
    try {
      const stored = localStorage.getItem('user');
      if (stored) {
        const u = JSON.parse(stored);
        return u.email || '';
      }
    } catch (e) {}
    return '';
  });
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [copiedAccount, setCopiedAccount] = useState<string | null>(null);

  // Verification Screen State
  const [submittedResult, setSubmittedResult] = useState<any | null>(null);

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedAccount(label);
    setTimeout(() => setCopiedAccount(null), 2500);
  };

  const handleSubmitProof = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subscriberEmail.trim()) {
      setErrorMsg('Please enter your email address so we can unlock your book download.');
      return;
    }
    if (!transactionId.trim()) {
      setErrorMsg('Please enter the Transaction ID (TRX / TID / Ref #) provided by your bank/app.');
      return;
    }

    setLoading(true);
    setErrorMsg('');

    try {
      const fd = new FormData();
      fd.append('amount', String(paymentData.amount));
      fd.append('type', paymentData.type);
      fd.append('publicationId', paymentData.id);
      fd.append('publicationTitle', paymentData.title || 'Legal Publication');
      fd.append('gateway', gateway);
      fd.append('senderName', senderName);
      fd.append('senderPhone', senderPhone);
      fd.append('transactionId', transactionId);
      fd.append('email', subscriberEmail.trim().toLowerCase());
      if (receiptFile) {
        fd.append('receipt', receiptFile);
      }

      const token = localStorage.getItem('token');
      const res = await api.post('/payments/submit-proof', fd, {
        headers: {
          'Content-Type': undefined,
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      if (res.data && res.data.success) {
        // Save pending ID to local storage so user sees pending badge immediately
        try {
          const storedPending = localStorage.getItem('elum_pending_publications') || '[]';
          const list = JSON.parse(storedPending);
          if (!list.includes(paymentData.id)) {
            list.push(paymentData.id);
            localStorage.setItem('elum_pending_publications', JSON.stringify(list));
          }
        } catch (err) {}

        setSubmittedResult(res.data);
      } else {
        setErrorMsg(res.data?.message || 'Failed to submit payment proof.');
      }
    } catch (err: any) {
      console.error('Submit payment proof error:', err);
      setErrorMsg(err.response?.data?.message || 'Could not submit payment transfer. Please check connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(2, 6, 23, 0.85)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 200000,
      backdropFilter: 'blur(6px)',
      padding: '1rem',
      boxSizing: 'border-box'
    }}>
      <div style={{
        backgroundColor: '#0f172a',
        border: '2px solid #ecc94b',
        borderRadius: '20px',
        maxWidth: '540px',
        width: '100%',
        position: 'relative',
        boxShadow: '0 25px 60px rgba(0, 0, 0, 0.9)',
        maxHeight: '94vh',
        overflowY: 'auto',
        color: '#f8fafc',
        boxSizing: 'border-box',
        padding: '2rem'
      }}>
        
        {/* CLOSE BUTTON */}
        <button 
          onClick={onClose} 
          style={{
            position: 'absolute',
            top: '1.25rem',
            right: '1.25rem',
            background: '#1e293b',
            border: '1px solid #334155',
            borderRadius: '8px',
            cursor: 'pointer',
            color: '#cbd5e1',
            padding: '0.4rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <X size={18} />
        </button>

        {!submittedResult ? (
          <>
            {/* HEADER */}
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{
                backgroundColor: 'rgba(236,201,75,0.15)',
                border: '1.5px solid #ecc94b',
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 0.75rem'
              }}>
                <ShieldCheck size={28} color="#ecc94b" />
              </div>
              <h3 style={{ fontSize: '1.4rem', color: '#ffffff', margin: '0 0 0.25rem 0', fontWeight: '900' }}>
                Subscription Payment &amp; Transfer Proof
              </h3>
              <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: 0 }}>
                Transfer subscription fee directly to the Owner/Superuser's account and submit your receipt below for verification.
              </p>
            </div>

            {/* ORDER SUMMARY */}
            <div style={{
              backgroundColor: '#070b14',
              border: '1.5px solid #1e293b',
              borderRadius: '12px',
              padding: '1rem 1.25rem',
              marginBottom: '1.25rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.35rem', fontSize: '0.82rem' }}>
                <span style={{ color: '#94a3b8' }}>Item / Monograph:</span>
                <span style={{ color: '#f8fafc', fontWeight: '800', textAlign: 'right', maxWidth: '65%' }}>
                  {paymentData.title || 'Legal Publication & Solution Manual'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '0.5rem', borderTop: '1px solid #1e293b' }}>
                <span style={{ color: '#cbd5e1', fontWeight: '800', fontSize: '0.9rem' }}>Subscription Amount:</span>
                <span style={{ color: '#ecc94b', fontWeight: '900', fontSize: '1.3rem' }}>
                  PKR {paymentData.amount.toLocaleString()}
                </span>
              </div>
            </div>

            {/* STEP 1: SELECT DEPOSIT METHOD */}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '800', fontSize: '0.82rem', color: '#cbd5e1', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                1. Select Owner / Superuser Deposit Account:
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', marginBottom: '0.75rem' }}>
                {/* JAZZCASH */}
                <button
                  type="button"
                  onClick={() => setGateway('JazzCash')}
                  style={{
                    padding: '0.65rem 0.5rem',
                    borderRadius: '10px',
                    border: gateway === 'JazzCash' ? '2px solid #ef4444' : '1px solid #334155',
                    backgroundColor: gateway === 'JazzCash' ? 'rgba(239,68,68,0.2)' : '#1e293b',
                    color: 'white',
                    fontWeight: '800',
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}
                >
                  <Wallet size={16} color={gateway === 'JazzCash' ? '#ef4444' : '#94a3b8'} />
                  <span>JazzCash</span>
                </button>

                {/* EASYPAISA */}
                <button
                  type="button"
                  onClick={() => setGateway('EasyPaisa')}
                  style={{
                    padding: '0.65rem 0.5rem',
                    borderRadius: '10px',
                    border: gateway === 'EasyPaisa' ? '2px solid #22c55e' : '1px solid #334155',
                    backgroundColor: gateway === 'EasyPaisa' ? 'rgba(34,197,94,0.2)' : '#1e293b',
                    color: 'white',
                    fontWeight: '800',
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}
                >
                  <Smartphone size={16} color={gateway === 'EasyPaisa' ? '#22c55e' : '#94a3b8'} />
                  <span>EasyPaisa</span>
                </button>

                {/* BANK TRANSFER */}
                <button
                  type="button"
                  onClick={() => setGateway('BankTransfer')}
                  style={{
                    padding: '0.65rem 0.5rem',
                    borderRadius: '10px',
                    border: gateway === 'BankTransfer' ? '2px solid #38bdf8' : '1px solid #334155',
                    backgroundColor: gateway === 'BankTransfer' ? 'rgba(56,189,248,0.2)' : '#1e293b',
                    color: 'white',
                    fontWeight: '800',
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}
                >
                  <Landmark size={16} color={gateway === 'BankTransfer' ? '#38bdf8' : '#94a3b8'} />
                  <span>Bank (IBAN)</span>
                </button>
              </div>

              {/* DESIGNATED ACCOUNT DETAILS BOX */}
              <div style={{
                backgroundColor: '#070b14',
                border: '1.5px solid #334155',
                borderRadius: '12px',
                padding: '1rem',
                position: 'relative'
              }}>
                {gateway === 'JazzCash' && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: '800', color: '#ef4444', textTransform: 'uppercase' }}>
                        🔴 Designated JazzCash Account:
                      </span>
                      <span style={{ fontSize: '0.7rem', backgroundColor: '#ef4444', color: 'white', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: '800' }}>
                        Superuser
                      </span>
                    </div>
                    <div style={{ fontSize: '0.85rem', fontWeight: '800', color: '#cbd5e1', marginBottom: '0.25rem' }}>
                      Account Title: <strong style={{ color: '#ecc94b' }}>NAJEEB ULLAH</strong>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#1e293b', padding: '0.5rem 0.75rem', borderRadius: '8px' }}>
                      <span style={{ fontSize: '1rem', fontWeight: '900', color: '#38bdf8', letterSpacing: '1px', fontFamily: 'monospace' }}>
                        0333 9389948
                      </span>
                      <button
                        type="button"
                        onClick={() => handleCopy('03339389948', 'jazz')}
                        style={{ background: 'none', border: 'none', color: '#ecc94b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem', fontWeight: '800' }}
                      >
                        {copiedAccount === 'jazz' ? <Check size={14} color="#4ade80" /> : <Copy size={14} />}
                        <span>{copiedAccount === 'jazz' ? 'Copied' : 'Copy'}</span>
                      </button>
                    </div>
                  </div>
                )}

                {gateway === 'EasyPaisa' && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: '800', color: '#22c55e', textTransform: 'uppercase' }}>
                        🟢 Designated EasyPaisa Account:
                      </span>
                      <span style={{ fontSize: '0.7rem', backgroundColor: '#22c55e', color: '#090d16', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: '900' }}>
                        Superuser
                      </span>
                    </div>
                    <div style={{ fontSize: '0.85rem', fontWeight: '800', color: '#cbd5e1', marginBottom: '0.25rem' }}>
                      Account Title: <strong style={{ color: '#ecc94b' }}>NAJEEB ULLAH</strong>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#1e293b', padding: '0.5rem 0.75rem', borderRadius: '8px' }}>
                      <span style={{ fontSize: '1rem', fontWeight: '900', color: '#4ade80', letterSpacing: '1px', fontFamily: 'monospace' }}>
                        0333 9389948
                      </span>
                      <button
                        type="button"
                        onClick={() => handleCopy('03339389948', 'easy')}
                        style={{ background: 'none', border: 'none', color: '#ecc94b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem', fontWeight: '800' }}
                      >
                        {copiedAccount === 'easy' ? <Check size={14} color="#4ade80" /> : <Copy size={14} />}
                        <span>{copiedAccount === 'easy' ? 'Copied' : 'Copy'}</span>
                      </button>
                    </div>
                  </div>
                )}

                {gateway === 'BankTransfer' && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: '800', color: '#38bdf8', textTransform: 'uppercase' }}>
                        🏛️ Allied Bank Limited (ABL) IBAN:
                      </span>
                      <span style={{ fontSize: '0.7rem', backgroundColor: '#0284c7', color: 'white', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: '800' }}>
                        Superuser
                      </span>
                    </div>
                    <div style={{ fontSize: '0.85rem', fontWeight: '800', color: '#cbd5e1', marginBottom: '0.25rem' }}>
                      Account Title: <strong style={{ color: '#ecc94b' }}>NAJEEB ULLAH</strong>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#1e293b', padding: '0.5rem 0.75rem', borderRadius: '8px' }}>
                      <span style={{ fontSize: '0.8rem', fontWeight: '800', color: '#38bdf8', fontFamily: 'monospace' }}>
                        PK75ABPA0010118975340013
                      </span>
                      <button
                        type="button"
                        onClick={() => handleCopy('PK75ABPA0010118975340013', 'bank')}
                        style={{ background: 'none', border: 'none', color: '#ecc94b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem', fontWeight: '800' }}
                      >
                        {copiedAccount === 'bank' ? <Check size={14} color="#4ade80" /> : <Copy size={14} />}
                        <span>{copiedAccount === 'bank' ? 'Copied' : 'Copy'}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* STEP 2: SENDER RECEIPT & DETAILS FORM */}
            <form onSubmit={handleSubmitProof} style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <label style={{ display: 'block', fontWeight: '800', fontSize: '0.82rem', color: '#cbd5e1', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                2. Provide Your Transfer Details &amp; Receipt Proof:
              </label>

              {/* EMAIL */}
              <div>
                <span style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem', fontWeight: '700' }}>
                  Your Email Address (Download License will be unlocked for this email) *
                </span>
                <input
                  type="email"
                  required
                  value={subscriberEmail}
                  onChange={(e) => setSubscriberEmail(e.target.value)}
                  placeholder="subscriber@example.com"
                  style={{
                    width: '100%',
                    padding: '0.65rem 0.85rem',
                    borderRadius: '8px',
                    border: '1px solid #334155',
                    backgroundColor: '#1e293b',
                    color: 'white',
                    fontSize: '0.88rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* SENDER NAME & PHONE */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <span style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem', fontWeight: '700' }}>
                    Sender Full Name *
                  </span>
                  <input
                    type="text"
                    required
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    placeholder="e.g. Aslam Khan"
                    style={{
                      width: '100%',
                      padding: '0.65rem 0.85rem',
                      borderRadius: '8px',
                      border: '1px solid #334155',
                      backgroundColor: '#1e293b',
                      color: 'white',
                      fontSize: '0.88rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <span style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem', fontWeight: '700' }}>
                    Sender Mobile / Account # *
                  </span>
                  <input
                    type="text"
                    required
                    value={senderPhone}
                    onChange={(e) => setSenderPhone(e.target.value)}
                    placeholder="e.g. 0333 1234567"
                    style={{
                      width: '100%',
                      padding: '0.65rem 0.85rem',
                      borderRadius: '8px',
                      border: '1px solid #334155',
                      backgroundColor: '#1e293b',
                      color: 'white',
                      fontSize: '0.88rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              {/* TRANSACTION ID (TRX / TID) */}
              <div>
                <span style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem', fontWeight: '700' }}>
                  Transaction Reference ID (TID / TRX / Ref # from SMS or App) *
                </span>
                <input
                  type="text"
                  required
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  placeholder="e.g. 984210349 or TRX-87612"
                  style={{
                    width: '100%',
                    padding: '0.65rem 0.85rem',
                    borderRadius: '8px',
                    border: '1px solid #334155',
                    backgroundColor: '#1e293b',
                    color: '#ecc94b',
                    fontWeight: '800',
                    fontSize: '0.92rem',
                    fontFamily: 'monospace',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* RECEIPT SLIP UPLOAD */}
              <div>
                <span style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem', fontWeight: '700' }}>
                  Attach Payment Transfer Screenshot / Deposit Slip (Optional but recommended)
                </span>
                <div style={{
                  border: '1.5px dashed #334155',
                  borderRadius: '8px',
                  padding: '0.75rem',
                  backgroundColor: '#070b14',
                  textAlign: 'center'
                }}>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setReceiptFile(e.target.files ? e.target.files[0] : null)}
                    style={{ color: '#cbd5e1', fontSize: '0.8rem' }}
                  />
                  {receiptFile && (
                    <div style={{ marginTop: '0.35rem', color: '#38bdf8', fontSize: '0.75rem', fontWeight: '800' }}>
                      Selected: {receiptFile.name} ({Math.round(receiptFile.size / 1024)} KB)
                    </div>
                  )}
                </div>
              </div>

              {errorMsg && (
                <div style={{ backgroundColor: 'rgba(239,68,68,0.2)', border: '1px solid #ef4444', color: '#fca5a5', padding: '0.5rem', borderRadius: '6px', fontSize: '0.8rem' }}>
                  ⚠️ {errorMsg}
                </div>
              )}

              {/* SUBMIT BUTTON */}
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '0.85rem 1.5rem',
                  backgroundColor: '#ecc94b',
                  color: '#0f172a',
                  border: 'none',
                  borderRadius: '10px',
                  fontWeight: '900',
                  fontSize: '0.95rem',
                  cursor: loading ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  marginTop: '0.5rem'
                }}
              >
                <CheckCircle size={18} />
                <span>
                  {loading ? 'Submitting Transfer Proof...' : 'Submit Payment Proof & Request Download Verification'}
                </span>
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', justifyContent: 'center', fontSize: '0.72rem', color: '#94a3b8' }}>
                <Info size={13} color="#ecc94b" />
                <span>Verification handled directly by Owner / Superuser (Advocate Najeeb Ullah)</span>
              </div>
            </form>
          </>
        ) : (
          /* STEP 3: SUBMISSION CONFIRMATION & TRACKING FEEDBACK */
          <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
            <div style={{
              backgroundColor: 'rgba(34,197,94,0.15)',
              border: '2px solid #22c55e',
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem'
            }}>
              <CheckCircle size={36} color="#22c55e" />
            </div>

            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              backgroundColor: 'rgba(234,179,8,0.2)',
              border: '1px solid #eab308',
              color: '#fef08a',
              padding: '0.25rem 0.75rem',
              borderRadius: '20px',
              fontSize: '0.75rem',
              fontWeight: '900',
              marginBottom: '0.75rem',
              letterSpacing: '0.5px'
            }}>
              <Clock size={13} />
              <span>AWAITING SUPERUSER / OWNER CONFIRMATION</span>
            </div>

            <h3 style={{ fontSize: '1.45rem', fontWeight: '900', color: '#ffffff', margin: '0 0 0.5rem 0' }}>
              Payment Transfer Proof Logged!
            </h3>

            <p style={{ fontSize: '0.85rem', color: '#cbd5e1', lineHeight: '1.5', margin: '0 0 1.25rem 0' }}>
              Your transfer reference has been registered with the database. The Owner/Superuser will verify that the subscription amount was received in account <strong>+92 333 9389948 (Najeeb Ullah)</strong>. Once confirmed, download access will be unlocked automatically for your account.
            </p>

            <div style={{
              backgroundColor: '#070b14',
              border: '1px solid #334155',
              borderRadius: '12px',
              padding: '1rem',
              textAlign: 'left',
              marginBottom: '1.5rem',
              fontSize: '0.82rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                <span style={{ color: '#94a3b8' }}>Publication:</span>
                <strong style={{ color: '#f8fafc' }}>{paymentData.title || 'Legal Publication'}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                <span style={{ color: '#94a3b8' }}>Amount:</span>
                <strong style={{ color: '#ecc94b' }}>PKR {paymentData.amount.toLocaleString()}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                <span style={{ color: '#94a3b8' }}>Transaction ID:</span>
                <strong style={{ color: '#38bdf8', fontFamily: 'monospace' }}>{transactionId}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                <span style={{ color: '#94a3b8' }}>Account Transferred To:</span>
                <strong style={{ color: '#4ade80' }}>+92 333 9389948 (NAJEEB ULLAH)</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#94a3b8' }}>Target Subscriber Email:</span>
                <strong style={{ color: '#f8fafc' }}>{subscriberEmail}</strong>
              </div>
            </div>

            <div style={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '10px', padding: '0.75rem', marginBottom: '1.5rem', fontSize: '0.78rem', color: '#cbd5e1', lineHeight: '1.4' }}>
              💡 <strong>Instant WhatsApp Verification:</strong> You may also forward your transfer screenshot to Advocate Najeeb Ullah on WhatsApp at <strong>+92 333 9389948</strong> for immediate confirmation.
            </div>

            <button
              onClick={() => {
                onSuccess();
                onClose();
              }}
              style={{
                width: '100%',
                padding: '0.8rem',
                borderRadius: '10px',
                border: 'none',
                backgroundColor: '#16a34a',
                color: 'white',
                fontWeight: '900',
                fontSize: '0.95rem',
                cursor: 'pointer'
              }}
            >
              Done &amp; Return to Downloads
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default PaymentModal;
